import { useToast } from '@capturi/ui-components'
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { getDisplayMessage } from 'components/api/displayMessage'
import { ThumbsButtons } from 'components/ThumbsButtons'
import { useMutateFeed } from 'copilot/useFeed'
import { type FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { MdCopyAll, MdRefresh } from 'react-icons/md'

import { useCaptureEvent } from '../../../utils/analytics'
import { MagicBox } from '../../components/MagicBox'
import { logEvents } from '../../logEvents'
import { DynamicTextArea } from './DynamicTextArea'
import { getSummaryErrorToast } from './summaryErrorToast'
import { useSummarize } from './useLiveSummary'
import { useSaveLiveSummary } from './useSaveLiveSummary'

type Props = {
  summary: string
  puzzelSessionUid: string
  apiToken: string
  uid: string
  queue: string
  agentUserName?: string
  idx: number
  lockSummaryEdit: boolean
  getTranscription: () => string
}

const opacity = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`

export const LiveSummary: FC<Props> = ({
  apiToken,
  summary,
  lockSummaryEdit,
  puzzelSessionUid,
  queue,
  agentUserName,
  uid,
  idx,
  getTranscription,
}) => {
  const captureEvent = useCaptureEvent()

  const toast = useToast()
  const { remove } = useMutateFeed(puzzelSessionUid)
  const [hide, setHide] = useState(false)
  const [hideButtons, setHideButtons] = useState(false)
  const [isReviewed, setIsReviewed] = useState(false)

  const [text, setText] = useState(summary || '')
  const live = useSummarize(apiToken)
  const { mutate, isPending, isSuccess } = useSaveLiveSummary(apiToken)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const shouldFocusOnMountRef = useRef<boolean>(Boolean(summary))

  useEffect(() => {
    logEvents({
      eventType: 'eventRenderLiveSummaryField',
    })
  }, [])

  // Auto-focus once on mount if there is initial text, without hijacking user selection later
  useLayoutEffect(() => {
    const textarea = textAreaRef.current
    if (!shouldFocusOnMountRef.current || !textarea) return
    textarea.focus()
    const end = textarea.value.length
    textarea.setSelectionRange(end, end)
    shouldFocusOnMountRef.current = false
  }, [])

  const handleSave = () => {
    captureEvent('copilot_liveSummary_save', {
      sessionUid: puzzelSessionUid,
      hasChanges: summary !== text,
      summaryLength: text.length,
      originalLength: summary.length,
    })
    mutate(
      {
        // Either field may be omitted if temporarily unavailable; see the
        // connect-time auth read in usePuzzelEvents.ts.
        metadata: {
          queueKey: queue.trim() || undefined,
          agentUserName: agentUserName?.trim() || undefined,
        },
        puzzelSessionUid,
        text,
      },
      {
        onSuccess: (resp) => {
          // Mirror a hedwig event so diagnostics can detect a concrete save
          logEvents({
            eventType: 'eventLiveSummarySaved',
            uid: resp?.uid,
            queue,
            summaryLength: text.length,
            hasChanges: summary !== text,
          })
        },
        onError: (error) => {
          toast({
            title: t`An error occurred while saving the summary`,
            description: getDisplayMessage(error),
            status: 'error',
          })
        },
      },
    )
  }

  const handleCopy = async () => {
    captureEvent('copilot_liveSummary_copy', {
      sessionUid: puzzelSessionUid,
      summaryLength: text.length,
      hasChanges: summary !== text,
    })
    try {
      await navigator.clipboard.writeText(text)
      toast({
        title: t`Copied summary to clipboard`,
        colorScheme: 'primary',
        size: 'sm',
        position: 'top',
      })
    } catch {
      //nothing
    }
  }

  return (
    <VStack
      alignItems="center"
      data-testid={`feed-item-${idx}`}
      width="100%"
      animation={`${opacity} 140ms ease-in`}
      transition="all ease 200ms"
      height={hide ? 0 : 'auto'}
      opacity={hide ? 0 : 100}
      overflow="hidden"
      onTransitionEnd={() => {
        if (hide) remove(uid)
      }}
    >
      <Box height="17px" width="1px" bg="greys.300" mt={2} />
      {isSuccess ? (
        <Box
          boxShadow="0px 0px 1px 0px #0000000A, 0px 0px 2px 0px #0000000F, 0px 4px 8px 0px #0000000A"
          border="solid 1px"
          backdropFilter="blur(50px)"
          bg="#ffffff17"
          borderRadius="8px"
          position="relative"
          overflow="hidden"
          borderColor="greys.100"
          p="1px"
          w="100%"
        >
          <Text py="2" px="4" lineHeight="short" whiteSpace="break-spaces">
            {text}
          </Text>
        </Box>
      ) : (
        <MagicBox p="1" dashed>
          <Flex alignItems="start" gap="2">
            <DynamicTextArea
              ref={textAreaRef}
              isDisabled={
                isPending || isSuccess || live.isPending || lockSummaryEdit
              }
              border="none"
              value={text}
              onChange={(e) => setText(e.currentTarget.value)}
              focusBorderColor="transparent"
              resize="none"
              flex="1"
              _focus={{
                borderColor: 'transparent',
                boxShadow: 'none',
                outline: 'none',
              }}
              data-testid={`live-summary-text-area-${idx}`}
            />
            {!isReviewed && text.trim() && (
              <HStack gap="1" pt="2" pr="2">
                <ThumbsButtons
                  onUp={() => {
                    captureEvent('copilot_liveSummary_thumbsUp', {
                      sessionUid: puzzelSessionUid,
                      summary: text,
                      summaryLength: text.length,
                    })
                    setIsReviewed(true)
                    toast({
                      title: t`Thank you for the feedback`,
                      description: t`You are helping us to improve future summaries`,
                      status: 'success',
                      duration: 4000,
                      isClosable: true,
                    })
                  }}
                  onDown={() => {
                    captureEvent('copilot_liveSummary_thumbsDown', {
                      sessionUid: puzzelSessionUid,
                      summary: text,
                      summaryLength: text.length,
                    })
                    setIsReviewed(true)
                    toast({
                      title: t`Thank you for the feedback`,
                      description: t`You are helping us to improve future summaries`,
                      status: 'success',
                      duration: 4000,
                      isClosable: true,
                    })
                  }}
                />
              </HStack>
            )}
          </Flex>
        </MagicBox>
      )}

      <Flex justifyContent="space-between" width="100%">
        <Flex>
          <Tooltip label={t`Copy`}>
            <IconButton
              variant="ghost"
              aria-label="copy"
              icon={<MdCopyAll />}
              onClick={handleCopy}
              colorScheme="primary"
            />
          </Tooltip>
          {!isSuccess && (
            <Tooltip label={t`Update summary`}>
              <IconButton
                colorScheme="primary"
                variant="ghost"
                aria-label="refetch"
                icon={<MdRefresh />}
                isLoading={live.isPending}
                isDisabled={isPending}
                onClick={() => {
                  captureEvent('copilot_liveSummary_update', {
                    sessionUid: puzzelSessionUid,
                    currentLength: text.length,
                    transcriptionLength: getTranscription().length,
                    hasChanges: summary !== text,
                  })
                  live.mutate(
                    { queue, transcription: getTranscription() },
                    {
                      onSuccess: (data) => data && setText(data.summary),
                      onError: (error) => {
                        toast({
                          ...getSummaryErrorToast(error),
                          status: 'error',
                        })
                      },
                    },
                  )
                }}
              />
            </Tooltip>
          )}
        </Flex>
        {!hideButtons && (
          <Box
            display="flex"
            transition="all 0.3s"
            opacity={isSuccess ? 0 : 100}
            overflow="hidden"
            height={isSuccess ? 0 : 'auto'}
            onTransitionEnd={() => {
              if (isSuccess) setHideButtons(true)
            }}
            mt="1"
          >
            <Button
              size="sm"
              variant="ghost"
              colorScheme="primary"
              isDisabled={isSuccess}
              onClick={() => {
                captureEvent('copilot_liveSummary_cancel', {
                  sessionUid: puzzelSessionUid,
                  summaryLength: text.length,
                  hasChanges: summary !== text,
                })

                setHide(true)
              }}
              data-testid={`live-summary-cancel-button-${idx}`}
            >
              <Trans>Delete</Trans>
            </Button>
            <Button
              size="sm"
              colorScheme="primary"
              isLoading={isPending}
              isDisabled={isSuccess}
              onClick={handleSave}
              data-testid={`live-summary-submit-button-${idx}`}
            >
              <Trans>Save summary</Trans>
            </Button>
          </Box>
        )}
      </Flex>
    </VStack>
  )
}
